import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { callFunction } from './callable';
import {
  REPORT_CATEGORIES,
  resolveCategoryMeta,
  resolveMarksPrivate,
  resolveReportIsSpam,
} from '../utils/reportCategories';

export function getReportCategories() {
  return REPORT_CATEGORIES;
}

export { resolveReportIsSpam, resolveMarksPrivate, resolveCategoryMeta };

export async function getPhoneNumber(canonicalId) {
  const ref = doc(db, 'phone_numbers', canonicalId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function upsertPhoneNumberStub(parsed) {
  const ref = doc(db, 'phone_numbers', parsed.canonicalId);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    return { id: existing.id, ...existing.data() };
  }

  const countryCode =
    parsed.countryCallingCode || parsed.country?.callingCode || '';
  const national = parsed.nationalNumber || '';
  const display =
    parsed.displayE164 ||
    (parsed.canonicalId ? `+${parsed.canonicalId}` : '');

  const data = {
    canonicalId: parsed.canonicalId,
    countryCallingCode: countryCode,
    nationalNumber: national,
    displayE164: display,
    countryName: parsed.countryName || parsed.country?.name || '',
    iso2: parsed.iso2 || parsed.country?.iso2 || '',
    type: 'unknown',
    isPrivate: false,
    verified: false,
    ownerId: null,
    spamScore: 0,
    trustScore: 0.5,
    scamScore: 0,
    nuisanceScore: 0,
    totalReports: 0,
    displayCompanyName: '',
    regionName: '',
    carrierName: '',
    createdAt: serverTimestamp(),
  };

  await setDoc(ref, data);
  return {
    id: parsed.canonicalId,
    ...data,
    lastReportedAt: null,
    createdAt: new Date().toISOString(),
  };
}

/** Batch-fetch existing numbers from a list of canonical IDs (for adjacent links). */
export async function getPhoneNumbersByIds(canonicalIds) {
  const results = await Promise.all(
    canonicalIds.map(async (id) => {
      const phone = await getPhoneNumber(id);
      return phone ? { ...phone, canonicalId: id } : null;
    })
  );
  return results.filter(Boolean);
}

/** Recent approved reports for public listings (excludes private numbers). */
export async function getRecentPublicReports(limitCount = 10) {
  const q = query(
    collection(db, 'reports'),
    where('status', '==', 'approved'),
    orderBy('createdAt', 'desc'),
    limit(40)
  );
  const snap = await getDocs(q);
  const reports = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const results = [];
  const seenPhones = new Set();
  const phoneCache = new Map();

  for (const report of reports) {
    if (results.length >= limitCount) break;

    const phoneId = report.phoneNumberId;
    if (!phoneId || seenPhones.has(phoneId)) continue;

    let phone = phoneCache.get(phoneId);
    if (phone === undefined) {
      phone = await getPhoneNumber(phoneId);
      phoneCache.set(phoneId, phone);
    }
    if (phone?.isPrivate) continue;

    seenPhones.add(phoneId);
    results.push({
      reportId: report.id,
      phoneNumberId: phoneId,
      displayE164: phone?.displayE164 || `+${phoneId}`,
      countryCallingCode: phone?.countryCallingCode || '',
      nationalNumber: phone?.nationalNumber || '',
      iso2: phone?.iso2 || '',
      categoryName: report.categoryName || report.categoryId,
      comment: report.comment,
      createdAt: report.createdAt,
      totalReports: phone?.totalReports ?? 0,
    });
  }

  return results;
}

export async function getApprovedReports(canonicalId) {
  const q = query(
    collection(db, 'reports'),
    where('phoneNumberId', '==', canonicalId),
    where('status', '==', 'approved'),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getPendingReports() {
  const q = query(
    collection(db, 'reports'),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc'),
    limit(100)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** User's active report for a number (excludes soft-deleted). */
export async function getMyReportForPhone(canonicalId, reporterId) {
  if (!canonicalId || !reporterId) return null;
  const q = query(
    collection(db, 'reports'),
    where('phoneNumberId', '==', canonicalId),
    where('reporterId', '==', reporterId),
    limit(5)
  );
  const snap = await getDocs(q);
  const doc = snap.docs.find((d) => d.data().status !== 'deleted');
  return doc ? { id: doc.id, ...doc.data() } : null;
}

/** Admin report lists via Cloud Function (bypasses client Firestore query limits). */
export async function listReportsForAdmin(filters = {}, limitCount = 100) {
  const status =
    typeof filters === 'string' ? filters : filters.status ?? 'all';
  const phoneNumberId =
    typeof filters === 'string' ? undefined : filters.phoneNumberId;
  const result = await callFunction('listReportsForAdmin', {
    status,
    ...(phoneNumberId ? { phoneNumberId } : {}),
    limit: limitCount,
  });
  return result?.reports ?? [];
}

/** @deprecated Use listReportsForAdmin('approved') */
export async function getPublishedReportsForAdmin(limitCount = 100) {
  return listReportsForAdmin('approved', limitCount);
}

export async function getReportsForOwner(canonicalIds) {
  if (!canonicalIds?.length) return [];
  const all = [];
  for (const id of canonicalIds.slice(0, 10)) {
    const q = query(
      collection(db, 'reports'),
      where('phoneNumberId', '==', id),
      orderBy('createdAt', 'desc'),
      limit(20)
    );
    const snap = await getDocs(q);
    snap.docs.forEach((d) => all.push({ id: d.id, ...d.data() }));
  }
  return all.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() || 0;
    const tb = b.createdAt?.toMillis?.() || 0;
    return tb - ta;
  });
}

export async function submitReport(payload) {
  const categoryId = payload.categoryId || 'spam-call';
  const meta = resolveCategoryMeta(categoryId);
  return callFunction('submitReport', {
    ...payload,
    categoryId,
    isSpam: meta.isSpam,
    marksPrivate: meta.marksPrivate,
  });
}

export async function updateReport(payload) {
  const categoryId = payload.categoryId || 'spam-call';
  const meta = resolveCategoryMeta(categoryId);
  return callFunction('updateReport', {
    ...payload,
    categoryId,
    isSpam: meta.isSpam,
    marksPrivate: meta.marksPrivate,
  });
}

export async function moderateReport(reportId, action) {
  return callFunction('moderateReport', { reportId, action });
}

export async function bulkModerateReports(reportIds, action) {
  return callFunction('bulkModerateReports', { reportIds, action });
}

export async function claimNumber(canonicalId, phoneMeta = null) {
  return callFunction('claimNumber', {
    canonicalId,
    ...(phoneMeta ? { phoneMeta } : {}),
  });
}

export async function sendVerificationCode(canonicalId) {
  return callFunction('sendVerificationCode', { canonicalId });
}

export async function verifyOwnershipCode(canonicalId, code) {
  return callFunction('verifyOwnershipCode', { canonicalId, code });
}

export async function updateNumberSettings(canonicalId, settings) {
  return callFunction('updateNumberSettings', {
    canonicalId,
    ...settings,
    ...(settings.businessName !== undefined
      ? { businessName: settings.businessName }
      : {}),
  });
}
